import {hoistCmp} from '@xh/hoist/core';
import {div, filler, hbox, span, vbox, vframe} from '@xh/hoist/cmp/layout';
import './Portfolio.scss';
import {chart} from '@xh/hoist/cmp/chart';
import {panel} from '@xh/hoist/desktop/cmp/panel';
import {toolbar} from '@xh/hoist/desktop/cmp/toolbar';
import {moneyFormatter} from '../../../../cmp/Formaters';
import {fmtNumber} from '@xh/hoist/format';
import {segmentedControl} from '@xh/hoist/desktop/cmp/input';
import {Icon} from '@xh/hoist/icon';
import {compactToolbarButton, compactToolbarSelect, toolbarButtonSeparator} from '../../../../cmp/toolbar/Toolbar';
import {PortfolioModel} from './PortfolioModel';
import {depositsDialog} from './DepositsDialog';
import {ManagementTypeEnum, Portfolio, RiskMetrics, TaxTreatmentEnum} from '../../../../data/DataTypes';
import classNames from 'classnames';
import {sparkline} from '../../../../cmp/miniCharts/MiniCharts';
import {intradaySparkline} from '../../../../cmp/miniCharts/IntradaySparkline';
import {kpiTile, moneyChangeChip, signDir, signedMoney} from '../../../../cmp/kpi/Kpi';
import {lightbulbDollarIcon, sackDollarIcon, userLockIcon} from '../../../../../core/Icons';

export const portfolio = hoistCmp.factory<PortfolioModel>({
    className: 'portfolio-field',
    render({model}) {
        return panel({
            modelConfig: {
                defaultSize: 420,
                collapsible: false,
                resizable: false,
                showSplitter: true,
                side: 'top'
            },
            item: vframe(portfolioSelector(), portfolioInfo(), portfolioChart(), depositsDialog({model: model.depositsDialog})),

            bbar: chartRange()
        });
    }
});

const portfolioSelector = hoistCmp.factory<PortfolioModel>(({model}) => {
    const className = 'portfolio-select';

    return toolbar({
        compact: true,
        items: [
            compactToolbarSelect({
                className,
                bind: 'portfolioId',
                options: model.portfolioOptions,
                labelField: 'portfolioName',
                valueField: 'id',
                placeholder: 'Select portfolio...',
                flex: 1,
                menuWidth: 300,
                optionRenderer: opt => {
                    const portfolio = opt as Portfolio;
                    return vbox({
                        className: classNames(`${className}__item`, portfolio.isActive ? '' : `${className}__item__inactive`),
                        items: [
                            hbox(portfolio.portfolioName, filler(), portfolio.isActive ? Icon.unlock({intent: 'primary'}) : Icon.lock()),
                            hbox({
                                className: `${className}__item__bottom-row`,
                                items: [
                                    span({
                                        item: portfolio.brokerage,
                                        className: `${className}__item__brokerage`
                                    }),
                                    filler(),
                                    portfolio.managementType == ManagementTypeEnum.Managed ? userLockIcon() : Icon.user({prefix: 'fal'}),
                                    portfolio.taxableType == TaxTreatmentEnum.Taxable ? sackDollarIcon() : lightbulbDollarIcon()
                                ]
                            })
                        ]
                    });
                }
            }),
            compactToolbarButton({
                icon: Icon.fileArchive(),
                title: 'Show/Hide Inactive Portfolios',
                active: model.showInactivePortfolios,
                onClick: () => model.toggleShowInactivePortfolios()
            }),
            toolbarButtonSeparator(),
            compactToolbarButton({icon: Icon.dollarSign({onClick: () => model.showDeposits()})})
        ]
    });
});

const portfolioInfo = hoistCmp.factory<PortfolioModel>({
    render({model}) {
        const {totals, risk} = model;

        return vbox({
            className: 'portfolio-fields__panel',
            items: [
                // Today's path on the left. Hero quote on the right - total balance over the day's move.
                hbox({
                    className: 'portfolio-fields__hero-row',
                    items: [
                        todaySparkline(model.portfolioId, totals.totalBalance),
                        vbox({
                            className: 'portfolio-fields__hero-quote',
                            items: [
                                div({className: 'portfolio-fields__hero', item: moneyFormatter(totals.totalBalance)}),
                                moneyChangeChip(totals.pnlDly, totals.pnlDlyPct)
                            ]
                        })
                    ]
                }),
                div({
                    className: 'portfolio-fields__kpis',
                    items: [
                        kpiTile('Positions', moneyFormatter(totals.marketValue)),
                        kpiTile('Cash', moneyFormatter(totals.cashBalance)),
                        kpiTile('G/L YTD', signedMoney(totals.pnlYtd), signDir(totals.pnlYtd)),
                        kpiTile('Margin', moneyFormatter(totals.marginBalance), totals.marginBalance ? 'down' : null),
                        kpiTile('Beta to SPX', fmtRisk(risk?.beta), null, null, riskTitle(risk, 'Beta of the daily returns to SPY')),
                        sharpeTile(risk)
                    ]
                }),
                allocationBar()
            ]
        });
    }
});

/**
 * Today's balance path as a sparkline, at a fixed height and half the row width. It starts at the prior close
 * and spans the whole session, so during the day the line reaches only as far as the current bar.
 * The series is polled once a minute, so during the session its last point is replaced by the live balance,
 * and the line always ends at the balance shown beside it. An empty box holds the space while the series loads.
 */
function todaySparkline(portfolioId: number, balance: number) {
    return intradaySparkline({
        portfolioId,
        renderer: (points, slots) => {
            // Only the prior close means no bar was sampled this session - nothing to draw, rather than a stub at the left edge
            if (!points || points.length < 2) return div({className: 'portfolio-spark'});
            const live = points.length < slots && balance != null ? [...points.slice(0, -1), balance] : points;
            return sparkline(live, 'portfolio-spark', slots) ?? div({className: 'portfolio-spark'});
        }
    });
}

function fmtRisk(v: number): string {
    return fmtNumber(v, {precision: 2, nullDisplay: '-'}) as string;
}

/** Tooltip for a risk tile: what it measures and the returns it is from, or why there is no value yet. */
function riskTitle(risk: RiskMetrics, what: string): string {
    if (!risk) return what;
    return risk.asOfDate
        ? `${what}, over ${risk.days} days of returns to ${risk.asOfDate}`
        : `${what}. Needs about three months of closing balances, and this portfolio has ${risk.days} days of returns.`;
}

/** Sharpe ratio, with the left edge colored by how it rates. The tooltip says what the rating means. */
function sharpeTile(risk: RiskMetrics) {
    const sharpe = risk?.sharpe,
        tier =
            sharpe == null
                ? null
                : sharpe > 2
                  ? {name: 'excellent', text: 'Above 2.0 - excellent, though usually seen in short bull runs rather than as a lasting level'}
                  : sharpe >= 1
                    ? {name: 'good', text: '1.0 to 2.0 - good to very good, strong returns for the volatility taken'}
                    : {name: 'standard', text: 'Below 1.0 - standard, typical of broad unhedged equity indexes over long periods'},
        title = riskTitle(risk, 'Sharpe ratio of the daily returns');

    return kpiTile('Sharpe', fmtRisk(sharpe), null, tier && `rzx-kpi--sharpe-${tier.name}`, tier ? `${title}. ${tier.text}` : title);
}

/** Positions vs. cash as a share of the total, with any margin shown in red. */
const allocationBar = hoistCmp.factory<PortfolioModel>(({model}) => {
    const {marketValue = 0, cashBalance = 0, marginBalance = 0} = model.totals,
        gross = Math.abs(marketValue) + Math.abs(cashBalance) + Math.abs(marginBalance);
    if (!gross) return null;

    const pct = (v: number) => `${((Math.abs(v) / gross) * 100).toFixed(1)}%`;
    return vbox({
        className: 'portfolio-alloc',
        items: [
            hbox({
                className: 'portfolio-alloc__bar',
                items: [
                    div({className: 'portfolio-alloc__seg portfolio-alloc__seg--positions', style: {width: pct(marketValue)}}),
                    div({className: 'portfolio-alloc__seg portfolio-alloc__seg--cash', style: {width: pct(cashBalance)}}),
                    div({className: 'portfolio-alloc__seg portfolio-alloc__seg--margin', style: {width: pct(marginBalance)}})
                ]
            }),
            hbox({
                className: 'portfolio-alloc__legend',
                items: [
                    span({className: 'portfolio-alloc__key portfolio-alloc__key--positions', item: `Positions ${pct(marketValue)}`}),
                    filler(),
                    span({className: 'portfolio-alloc__key portfolio-alloc__key--cash', item: `Cash ${pct(cashBalance)}`})
                ]
            })
        ]
    });
});

const portfolioChart = hoistCmp.factory<PortfolioModel>({
    render({model}) {
        const {rangeChange} = model;

        return panel({
            className: 'portfolio-chart__panel',
            width: '100%',
            flex: 1,
            minHeight: 120,
            items: [
                chart(),
                // Balance change over the selected range, overlaid on the chart's top-left corner.
                rangeChange ? div({className: 'portfolio-chart__change', item: moneyChangeChip(rangeChange.amount, rangeChange.pct)}) : null
            ]
        });
    }
});

const chartRange = hoistCmp.factory<PortfolioModel>({
    render({model}) {
        return toolbar({
            compact: true,
            className: 'portfolio-range',
            items: [
                segmentedControl({
                    className: 'portfolio-range__buttons',
                    bind: 'range',
                    outlined: false,
                    showTrayBackground: false,
                    options: ['1M', '3M', '6M', 'YTD', '1Y', {value: '', label: 'ALL'}]
                })
            ]
        });
    }
});
